import { api } from "../api";
import type {
  Coach,
  CoachStatus,
  CoachType,
  SocialLink,
  SocialPlatform,
} from "../schema";
import { InviteCoachData } from "../schema";

// ponytail: one page large enough for every coach, so pickers need no search.
// Move pickers to search-as-you-type once coaches reach the hundreds.
export const COACH_PICKER_LIMIT = 200;

// Server returns User objects with coachProfile, but we need to transform to Coach format
interface ServerCoachStats {
  publishedCount: number;
  totalSessions: number;
  audioCount: number;
  videoCount: number;
  blogCount: number;
  recipeCount: number;
  affirmationCount: number;
  challengeCount: number;
  journeyCount: number;
}

interface ServerCoachResponse {
  id: string;
  email: string;
  name: string | null;
  roles: string[];
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string | null;
  socialLinks?: { id: string; platform: string; url: string }[];
  coachProfile: {
    id?: string;
    userId?: string;
    avatarUrl?: string;
    bio?: string;
    expertise?: string[];
    languages?: string[];
    certifications?: string[];
    status?: "active" | "suspended" | "deactivated";
    statusReason?: string;
    coachType?: string;
    city?: string;
    state?: string;
    country?: string;
    invitedAt?: string;
  } | null;
  stats?: ServerCoachStats;
}

export interface PaginatedCoachesResponse {
  coaches: Coach[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type CoachContentType =
  | "audio"
  | "video"
  | "blog"
  | "recipe"
  | "affirmation"
  | "challenge"
  | "journey";

export interface CoachContentListItem {
  id: string;
  contentType: CoachContentType;
  title: string;
  thumbnailUrl: string | null;
  publishedAt: string | null;
}

export interface AuditLogEntry {
  id: string;
  description: string;
  adminUsername: string;
  adminRole: string;
  timestamp: string;
}

export interface AcceptInvitationInput {
  token: string;
  password: string;
  name?: string;
  bio?: string;
  expertise?: string[];
  languages?: string[];
  certifications?: string[];
  city?: string;
  state?: string;
  country?: string;
  avatar?: File;
}

export interface AcceptInvitationResponse {
  message: string;
  user: ServerCoachResponse;
}

// Transform server response to Coach format
function transformServerCoachToCoach(serverCoach: ServerCoachResponse): Coach {
  const profile = serverCoach.coachProfile || {};
  // Convert dates from ISO strings to the format expected by the frontend (ISO string or null)
  const formatDate = (
    date: string | Date | undefined | null,
  ): string | null => {
    if (!date) return null;
    if (typeof date === "string") return date;
    return date.toISOString();
  };

  const socialLinks: SocialLink[] = (serverCoach.socialLinks || [])
    .filter((link) => link.platform && link.url)
    .map((link) => ({
      platform: link.platform as SocialPlatform,
      url: link.url,
    }));

  const stats = serverCoach.stats;

  return {
    id: serverCoach.id,
    userId: profile.userId || serverCoach.id,
    name: serverCoach.name || "",
    email: serverCoach.email,
    bio: profile.bio || "",
    photoUrl: profile.avatarUrl || null,
    expertise: profile.expertise || [],
    languages: profile.languages || [],
    certifications: profile.certifications || [],
    coachType: (profile.coachType || "coach") as CoachType,
    status: (profile.status || "active") as CoachStatus,
    statusReason: profile.statusReason || null,
    draftsCount: 0,
    publishedCount: stats?.publishedCount ?? 0,
    contentCount: stats?.publishedCount ?? 0,
    totalSessions: stats?.totalSessions ?? 0,
    totalViews: 0,
    totalCompletions: 0,
    invitedAt: formatDate(profile.invitedAt),
    createdAt: formatDate(serverCoach.createdAt),
    lastActiveAt: formatDate(serverCoach.lastLoginAt),
    lastSubmissionAt: null, // Not available from server
    pendingProfileChanges: null, // Not available from server
    pendingProfileSubmittedAt: null, // Not available from server
    socialLinks,
    city: profile.city || undefined,
    state: profile.state || undefined,
    country: profile.country || undefined,
  };
}

export const coachService = {
  async getCoaches(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {},
  ): Promise<PaginatedCoachesResponse> {
    try {
      const { page = 1, limit = COACH_PICKER_LIMIT, search, status } = params;
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(limit));
      if (search && search.trim()) {
        qs.set("search", search.trim());
      }
      if (status && status !== "all") {
        qs.set("status", status);
      }
      const response = await api.get<{
        coaches: ServerCoachResponse[];
        pagination: any;
      }>(`/coaches?${qs.toString()}`);
      return {
        coaches: response.coaches.map(transformServerCoachToCoach),
        pagination: response.pagination,
      };
    } catch (error) {
      console.error("Failed to fetch coaches:", error);
      throw error;
    }
  },

  async getCoachById(id: string): Promise<Coach | null> {
    try {
      const response = await api.get<ServerCoachResponse>(`/coaches/${id}`);
      return transformServerCoachToCoach(response);
    } catch (error) {
      console.error("Failed to fetch coach by ID:", error);
      return null;
    }
  },

  async getCoachContent(id: string): Promise<{
    items: CoachContentListItem[];
    total: number;
  }> {
    return api.get<{ items: CoachContentListItem[]; total: number }>(
      `/coaches/${id}/content`,
    );
  },

  async createCoach(coachData: Partial<Coach>): Promise<Coach> {
    const response = await api.post<ServerCoachResponse>("/coaches", coachData);
    return transformServerCoachToCoach(response);
  },

  async updateCoach(
    id: string,
    coachData: Partial<Coach>,
    file?: File,
  ): Promise<Coach> {
    const formData = new FormData();
    if (file) {
      formData.append("avatar", file);
    }

    Object.entries(coachData).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === "socialLinks" && Array.isArray(value)) {
        value.forEach((link, index) => {
          formData.append(`socialLinks[${index}][platform]`, link.platform);
          formData.append(`socialLinks[${index}][url]`, link.url);
        });
      } else if (Array.isArray(value)) {
        // An empty array appends nothing, so the server would keep the old
        // list. One "" entry clears expertise; the server drops empty strings.
        if (key === "expertise" && value.length === 0) {
          formData.append("expertise[]", "");
        }
        value.forEach((item) => formData.append(`${key}[]`, item));
      } else {
        formData.append(key, String(value));
      }
    });

    const response = await api.put<ServerCoachResponse>(
      `/coaches/${id}`,
      formData,
    );
    return transformServerCoachToCoach(response);
  },

  async deleteCoach(id: string, reason: string): Promise<void> {
    try {
      await api.delete(`/coaches/${id}`, { reason });
    } catch (error) {
      console.error("Failed to delete coach:", error);
      throw error;
    }
  },

  async restoreDeletedCoach(id: string): Promise<Coach> {
    const response = await api.post<ServerCoachResponse>(
      `/coaches/${id}/restore`,
      {},
      false, // Requires authentication
    );
    return transformServerCoachToCoach(response);
  },

  async inviteCoach(
    data: InviteCoachData,
  ): Promise<{ message: string; invitation: any }> {
    const response = await api.post<{ message: string; invitation: any }>(
      "/coaches/invite",
      data,
    );
    return response;
  },

  async suspendCoach(id: string, reason: string): Promise<Coach> {
    const response = await api.put<ServerCoachResponse>(`/coaches/${id}`, {
      status: "suspended",
      statusReason: reason,
    });
    return transformServerCoachToCoach(response);
  },

  async restoreCoach(id: string): Promise<Coach> {
    const response = await api.put<ServerCoachResponse>(`/coaches/${id}`, {
      status: "active",
      statusReason: null,
    });
    return transformServerCoachToCoach(response);
  },

  async validateInvitation(token: string): Promise<{
    email: string;
    expiresAt: string;
    coachType?: string;
    name?: string;
  }> {
    const response = await api.get<{
      email: string;
      expiresAt: string;
      coachType?: string;
      name?: string;
    }>(
      `/coaches/validate-invitation/${token}`,
      true, // skipAuth = true for public endpoint
    );
    return response;
  },

  async acceptInvitation(
    data: AcceptInvitationInput,
  ): Promise<AcceptInvitationResponse> {
    const formData = new FormData();
    formData.append("token", data.token);
    formData.append("password", data.password);
    if (data.name) formData.append("name", data.name);
    if (data.bio) formData.append("bio", data.bio);
    if (data.expertise && data.expertise.length > 0) {
      data.expertise.forEach((exp) => formData.append("expertise", exp));
    }
    if (data.languages && data.languages.length > 0) {
      data.languages.forEach((lang) => formData.append("languages", lang));
    }
    if (data.certifications && data.certifications.length > 0) {
      data.certifications.forEach((cert) =>
        formData.append("certifications", cert),
      );
    }
    if (data.city) formData.append("city", data.city);
    if (data.state) formData.append("state", data.state);
    if (data.country) formData.append("country", data.country);
    if (data.avatar) formData.append("avatar", data.avatar);

    // Note: This endpoint is public and doesn't require authentication
    const response = await api.post<{
      message: string;
      user: ServerCoachResponse;
    }>(
      "/coaches/accept-invitation",
      formData,
      true, // skipAuth = true for public endpoint
    );
    return response;
  },
};
